// XSS Protection - Input Sanitization
export const sanitizeInput = (input: string): string => {
  if (!input) return '';
  
  // Remove HTML tags
  let sanitized = input.replace(/<[^>]*>/g, '');
  
  // Encode special characters
  const entityMap: { [key: string]: string } = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
    '/': '&#x2F;',
    '`': '&#x60;',
    '=': '&#x3D;'
  };
  
  sanitized = sanitized.replace(/[&<>"'`=/]/g, (s) => entityMap[s]);
  
  return sanitized.trim();
};

export const sanitizeEmail = (email: string): string => {
  // Basic email validation + sanitization
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    throw new Error('Invalid email format');
  }
  return sanitizeInput(email);
};

export const sanitizeName = (name: string): string => {
  // Allow only letters, spaces, hyphens
  const sanitized = name.replace(/[^a-zA-Z\s-]/g, '');
  return sanitized.trim().substring(0, 100); // Max 100 chars
};

export const sanitizeAPIKey = (key: string): string => {
  // API keys should only contain alphanumeric + hyphens
  const sanitized = key.replace(/[^a-zA-Z0-9-]/g, '');
  return sanitized.trim();
};

export const sanitizeIP = (ip: string): string => {
  // Basic IPv4 validation
  const ipRegex = /^(\d{1,3}\.){3}\d{1,3}$/;
  if (!ipRegex.test(ip)) {
    throw new Error('Invalid IP format');
  }
  
  // Validate each octet
  const octets = ip.split('.');
  for (const octet of octets) {
    const num = parseInt(octet);
    if (num < 0 || num > 255) {
      throw new Error('Invalid IP range');
    }
  }
  
  return ip;
};

// Prevent prototype pollution
export const sanitizeObject = (obj: any): any => {
  if (typeof obj !== 'object' || obj === null) return obj;
  
  const sanitized: any = Array.isArray(obj) ? [] : {};
  
  for (const key in obj) {
    // Skip dangerous keys
    if (key === '__proto__' || key === 'constructor' || key === 'prototype') {
      continue;
    }
    
    if (obj.hasOwnProperty(key)) {
      const value = obj[key];
      
      if (typeof value === 'string') {
        sanitized[key] = sanitizeInput(value);
      } else if (typeof value === 'object') {
        sanitized[key] = sanitizeObject(value);
      } else {
        sanitized[key] = value;
      }
    }
  }
  
  return sanitized;
};

// Content Security Policy Helper
export const sanitizeHTML = (html: string): string => {
  // Use DOMPurify-like approach
  const temp = document.createElement('div');
  temp.textContent = html; // textContent auto-escapes
  return temp.innerHTML;
};